import { CheckboxFilter } from "@components/agency/CheckboxFilter";
import { agencyData } from "@components/agency/constant";
import { LocationData, LocationFilter } from "@components/agency/LocationFilter";
import { AlternativesTopBannerCTASection } from "@components/alternatives/AlternativesTopBannerCTASection";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import NewLayout from "@components/new-ui/Layout";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { AgencyDetailsWithLocation } from "types/agencies";
import { HerosectionProps } from "types/agencies";

interface AgencyProps {
    selectedCountry?: string;
    selectedCity?: string;
    title: string;
    description: string;
    canonical: string;
}

interface AgencyCardProps {
    agency: AgencyDetailsWithLocation;
}

const getAllUnique = (key: "service" | "industry") => {
    const set = new Set<string>();
    agencyData.forEach((country) =>
        country.cities.forEach((city) =>
            city.agencies.forEach((agency) => {
                agency[key].forEach((item) => set.add(item));
            })
        )
    );
    return Array.from(set);
};

export const Herosection: React.FC<HerosectionProps> = ({ selectedCity, selectedCountry }) => {
    const locationText = selectedCity || selectedCountry || null;
    return (
        <section
            className="text-white md:pt-40 md:pb-28 py-16"
            style={{
                background: "linear-gradient(265deg, #A1D8FF -7.73%, #396AFF 19.04%, #00238D 61.31%, #000 102.16%)",
            }}
        >
            <div className={`${container_classname}`}>
                <div className={"text-sm text-white mb-8"}>
                    <a href="/" className="hover:underline text-white">
                        Home{" "}
                    </a>
                    <span>/</span>
                    <a href="/agencies" className="hover:underline text-white">
                        {" "}
                        Agencies{" "}
                    </a>
                    {selectedCountry && (
                        <>
                            <span>/</span>
                            <a href={`/agencies/${selectedCountry.toLowerCase()}`} className="hover:underline text-white">
                                {" "}
                                {selectedCountry}{" "}
                            </a>
                        </>
                    )}

                    {selectedCity && (
                        <>
                            <span>/</span> <p className=" inline-block">{selectedCity}</p>
                        </>
                    )}
                </div>
                <h1 className="text-gray-50 lg:text-new-h1 md:text-5xl text-4xl font-bold gap-2 md:mb-6 mb-4" style={{ fontFamily: "Degular Variable" }}>
                    Best WhatsApp marketing <br />
                    agencies {locationText ? `in ${locationText}` : null}
                </h1>
                <p>
                    Find the top-rated WhatsApp marketing agencies in {selectedCountry} {selectedCity ? `/ ${selectedCity}` : ""}. Compare services, pricing, and reviews to find
                    the best partner for your business.
                </p>
            </div>
        </section>
    );
};

const AgencyCard: React.FC<AgencyCardProps> = ({ agency }) => {
    return (
        <div className="border border-solid border-gray-200 p-5 mt-4 rounded">
            <div className="flex mb-6 gap-x-5">
                <Image src={agency.imageUrl} width={80} height={80} alt={agency.name} />
                <div>
                    <p className="font-bold text-3xl">{agency.name}</p>
                    <p className=" text-gray-600 mb-2 mt-2">
                        {agency.country}, {agency.cityName}
                    </p>
                </div>
            </div>
            <p className=" text-gray-700 mb-5">{agency.about}</p>

            <div className="md:grid md:grid-cols-6 gap-2 flex mb-2">
                <p className=" text-gray-600">Services:</p>
                <p className="md:col-span-5  text-black">
                    {agency.service.map((service, idx) => (
                        <span key={idx}>
                            <span className="font-medium">{service}</span>
                            {idx < agency.service.length - 1 && <span className="font-normal text-gray-500"> / </span>}
                        </span>
                    ))}
                </p>
            </div>

            <div className="md:grid md:grid-cols-6 gap-2 flex">
                <p className=" text-gray-600">Industries:</p>
                <p className="md:col-span-5 text-black">
                    {agency.industry.map((industry, idx) => (
                        <span key={idx}>
                            <span className="font-medium">{industry}</span>
                            {idx < agency.industry.length - 1 && <span className="font-normal text-gray-500"> / </span>}
                        </span>
                    ))}
                </p>
            </div>

            <div className="md:grid md:grid-cols-6 mb-2 mt-2 flex gap-2">
                <p className=" text-gray-600">Budget:</p>
                <p className="md:col-span-5  text-black font-medium">{agency.budget}</p>
            </div>
            <div className="md:grid md:grid-cols-6 mb-2 mt-2 flex gap-2">
                <p className=" text-gray-600">Founded:</p>
                <p className="md:col-span-5  text-black font-medium">{agency.foundedYear}</p>
            </div>
            <div className="md:grid md:grid-cols-6 mb-2 mt-2 flex gap-2">
                <p className=" text-gray-600">Website:</p>
                <p className="md:col-span-5  text-gray-600">{agency.website}</p>
            </div>
        </div>
    );
};

const budgetOptions = ["INR 1500+", "INR 1000+", "INR 1200+", "INR 5000+", "INR 7500+", "INR 4500+", "INR 15000+", "INR 2500+"];

const AgencyDirectory: React.FC<AgencyProps> = ({ selectedCountry: initialCountry = "India", selectedCity: initialCity = "", title, description, canonical }) => {
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [selectedCity, setSelectedCity] = useState(initialCity);
    const [selectedServices, setSelectedServices] = useState<string[]>([]);
    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
    const [selectedBudget, setSelectedBudget] = useState("");
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [arrOfCurrButtons, setArrOfCurrButtons] = useState<(number | string)[]>([]);

    const allServices = getAllUnique("service");
    const allIndustries = getAllUnique("industry");

    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;

    const resetFilters = () => {
        setSelectedCountry("");
        setSelectedCity("");
        setSelectedServices([]);
        setSelectedIndustries([]);
        setSelectedBudget("");
    };

    const filteredAgencies = agencyData
        .filter((c) => (selectedCountry ? c.country.toLowerCase() === selectedCountry.toLowerCase() : true))
        .flatMap((c) =>
            c.cities.flatMap((city) =>
                selectedCity && selectedCity.toLowerCase() !== city.cityName.toLowerCase()
                    ? []
                    : city.agencies
                          .filter(
                              (agency) =>
                                  (selectedServices.length === 0 || agency.service.some((s) => selectedServices.includes(s))) &&
                                  (selectedIndustries.length === 0 || agency.industry.some((i) => selectedIndustries.includes(i))) &&
                                  (selectedBudget === "" || agency.budget === selectedBudget)
                          )
                          .map((agency) => ({
                              ...agency,
                              country: c.country,
                              cityName: city.cityName,
                          }))
            )
        );

    const pageCount = Math.ceil(filteredAgencies.length / itemsPerPage);
    const pageNumbers = Array.from({ length: pageCount }, (_, i) => i + 1);

    useEffect(() => {
        const dotsInitial = "...";
        const dotsLeft = "... ";
        const dotsRight = " ...";
        let tempNumberOfButtons: (number | string)[] = [];

        if (pageCount < 6) {
            tempNumberOfButtons = pageNumbers;
        } else if (currentPage + 1 <= 3) {
            tempNumberOfButtons = [1, 2, 3, 4, dotsInitial, pageCount];
        } else if (currentPage + 1 === 4) {
            const sliced = pageNumbers.slice(0, 5);
            tempNumberOfButtons = [...sliced, dotsInitial, pageCount];
        } else if (currentPage + 1 > 4 && currentPage + 1 < pageCount - 2) {
            const sliced1 = pageNumbers.slice(currentPage - 1, currentPage + 2);
            tempNumberOfButtons = [1, dotsLeft, ...sliced1, dotsRight, pageCount];
        } else {
            const sliced = pageNumbers.slice(pageCount - 4);
            tempNumberOfButtons = [1, dotsLeft, ...sliced];
        }

        setArrOfCurrButtons(tempNumberOfButtons);
    }, [currentPage, pageCount, pageNumbers]);

    const handlePaginationClick = (button: number | string) => {
        if (typeof button === "number") {
            setCurrentPage(button - 1);
        } else if (button === "...") {
            setCurrentPage(arrOfCurrButtons[arrOfCurrButtons.length - 3] as number);
        } else if (button === " ...") {
            setCurrentPage((arrOfCurrButtons[3] as number) + 1);
        } else if (button === "... ") {
            setCurrentPage((arrOfCurrButtons[3] as number) - 3);
        }
    };

    useEffect(() => {
        setCurrentPage(0);
    }, [selectedCountry, selectedCity, selectedServices, selectedIndustries, selectedBudget]);

    const offset = currentPage * itemsPerPage;
    const currentItems = filteredAgencies.slice(offset, offset + itemsPerPage);

    return (
        <NewLayout
            title={title || "The Best WhatsApp Marketing Agencies Around You | Gallabox"}
            description={
                description || "Find the top-rated WhatsApp marketing agencies in your city. Compare services, pricing, and reviews to find the best partner for your business."
            }
            canonical={canonical || "https://gallabox.com/agencies"}
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/agencies/gallabox-agencies-og.png" }}
        >
            <div>
                <Herosection selectedCity={initialCity} selectedCountry={initialCountry} />
                <section className="md:sticky md:top-16 md:z-3 h-28 bg-white pt-10 pb-10 md:block hidden shadow-md border-t border-solid border-gray-100">
                    <div className={`${container_classname} flex md:flex-row  flex-wrap  gap-4 px-1 items-center`}>
                        <button onClick={resetFilters} className="bg-blue-700 text-white px-4 py-2 md:py-1 rounded-md hover:bg-blue-600">
                            All
                        </button>

                        <LocationFilter selectedCountry={selectedCountry} selectedCity={selectedCity} />

                        <CheckboxFilter
                            title="Services"
                            items={allServices}
                            selectedItems={selectedServices}
                            setSelectedItems={setSelectedServices}
                            filterName="services"
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                        />

                        <CheckboxFilter
                            title="Industries"
                            items={allIndustries}
                            selectedItems={selectedIndustries}
                            setSelectedItems={setSelectedIndustries}
                            filterName="industries"
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                        />

                        <select value={selectedBudget} onChange={(e) => setSelectedBudget(e.target.value)} className="border md:px-3 py-1 rounded m-0 w-fit">
                            <option value="">All Budgets</option>
                            {budgetOptions.map((b) => (
                                <option key={b} value={b}>
                                    {b.replace("INR ", "")}
                                </option>
                            ))}
                        </select>
                    </div>
                </section>

                <section className="mt-20 w-full px-20 md:hidden block">
                    <div
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="text-center border border-solid border-gray-200 px-2 py-3 rounded-md mb-4 font-semibold cursor-pointer"
                    >
                        Filter
                    </div>

                    {isFilterOpen && (
                        <div className="flex flex-col gap-5">
                            <button onClick={resetFilters} className="bg-blue-500 text-white px-2 py-3 md:py-1 rounded-md hover:bg-blue-600 w-full">
                                ALL
                            </button>

                            <LocationFilter selectedCountry={selectedCountry} selectedCity={selectedCity} />

                            <CheckboxFilter
                                title="Services"
                                items={allServices}
                                selectedItems={selectedServices}
                                setSelectedItems={setSelectedServices}
                                filterName="services"
                                openDropdown={openDropdown}
                                setOpenDropdown={setOpenDropdown}
                            />

                            <CheckboxFilter
                                title="Industries"
                                items={allIndustries}
                                selectedItems={selectedIndustries}
                                setSelectedItems={setSelectedIndustries}
                                filterName="industries"
                                openDropdown={openDropdown}
                                setOpenDropdown={setOpenDropdown}
                            />

                            <select value={selectedBudget} onChange={(e) => setSelectedBudget(e.target.value)} className="border md:px-3 py-1 rounded m-0 md:w-fit w-full">
                                <option value="">All Budgets</option>
                                {budgetOptions.map((b) => (
                                    <option key={b} value={b}>
                                        {b.replace("INR ", "")}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </section>

                <section>
                    <section className="md:max-w-6xl flex md:flex-row flex-col md:mx-auto pt-16 px-5 md:px-0 mt-10 md:mt-0">
                        <div className="md:w-3/4 w-full">
                            {currentItems.length > 0 ? (
                                <>
                                    {currentItems.map((agency, index) => (
                                        <AgencyCard key={index} agency={agency} />
                                    ))}
                                </>
                            ) : (
                                <p className="mt-4 text-center text-gray-500">No agencies match the selected filters.</p>
                            )}

                            <div className="flex justify-center mt-6 space-x-2">
                                <button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                                    disabled={currentPage === 0}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Prev
                                </button>

                                {arrOfCurrButtons.map((btn, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handlePaginationClick(btn)}
                                        className={`px-3 py-1 border rounded ${btn === currentPage + 1 ? "bg-blue-500 text-white" : ""}`}
                                    >
                                        {btn}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pageCount - 1))}
                                    disabled={currentPage === pageCount - 1}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        <div className="h-96 md:block hidden" style={{ position: "sticky", top: "250px", minHeight: "500px" }}>
                            <AlternativesTopBannerCTASection
                                ctaTopSection={{
                                    content: "Automate sales through WhatsApp for your clients",
                                    imageUrl: "https://assets.gallabox.com/gb-home/alternatives/whatsapp-with-gallabox.png",
                                    imgAlt: "whatsapp-with-gallabox",
                                    btnText: "Get Gallabox",
                                }}
                            />
                        </div>
                    </section>

                    <div className={container_classname}>
                        <p className="mt-20 font-semibold text-4xl">By city</p>
                        {LocationData.map((loc, idx) => (
                            <div key={idx} className="mt-10 mb-20 w-3/4 gap-y-4 md:flex md:flex-wrap grid grid-cols-2 md:gap-5">
                                {loc.cities.map((city) => (
                                    <a
                                        key={city.cityName}
                                        href={`/agencies/${loc.country.toLowerCase()}/${city.cityName.toLowerCase()}`}
                                        className="block ml-5 sm:ml-0 px-3 py-1 hover:bg-gray-100 cursor-pointer border border-solid border-gray-200 rounded-full w-fit"
                                    >
                                        {city.cityName}
                                    </a>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="mt-20">
                        <CTASection title={"Grab student attention on their favorite channel.Just text them already."} />
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default AgencyDirectory;
